import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mic, Play, Pause, Loader2, RotateCcw, Save, Sparkles } from "lucide-react";
import api from "../lib/api";
import audioManager from "../lib/audioManager";
import ThemedSelect from "./ThemedSelect";

/**
 * Iter-27.49 — Customisation backoffice du message audio de bienvenue.
 *
 * Permet à l'admin de :
 *  - Réécrire le texte du message de bienvenue (textarea avec compteur de chars)
 *  - Choisir la voix (réutilise la voice library, voix custom incluses)
 *  - Ajuster la vitesse (slider 0.7-1.2×)
 *  - Tester en preview AVANT de sauvegarder
 *  - Restaurer le texte par défaut Shariti
 */
export default function OnboardingAudioSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [voices, setVoices] = useState([]);
    const [defaults, setDefaults] = useState({ text: "", voice_id: "", speed: 1.05 });
    const [form, setForm] = useState({ text: "", voice_id: "", speed: 1.05 });
    const audioRef = useRef(null);
    const handleRef = useRef(null);
    if (!handleRef.current) handleRef.current = audioManager.acquire("admin-onboarding-preview");

    useEffect(() => {
        (async () => {
            try {
                const [setRes, voiceRes] = await Promise.all([
                    api.get("/admin/onboarding/audio-settings"),
                    api.get("/admin/tts/voices"),
                ]);
                setDefaults(setRes.data.defaults);
                setForm(setRes.data.current);
                setVoices(voiceRes.data.voices || []);
            } catch {
                toast.error("Impossible de charger les paramètres audio");
            } finally {
                setLoading(false);
            }
        })();
        return () => {
            if (audioRef.current) {
                try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* */ }
                audioRef.current = null;
            }
            handleRef.current?.release();
        };
    }, []);

    const handlePreview = async () => {
        // Toggle pause si déjà en cours
        if (playing && audioRef.current) {
            audioRef.current.pause();
            setPlaying(false);
            return;
        }
        // Resume si paused
        if (audioRef.current && audioRef.current.paused && audioRef.current.currentTime > 0) {
            handleRef.current.attach(audioRef.current);
            try {
                await audioRef.current.play();
                setPlaying(true);
            } catch { /* */ }
            return;
        }
        setPreviewing(true);
        try {
            const formData = new FormData();
            formData.append("text", form.text);
            formData.append("voice_id", form.voice_id || defaults.voice_id);
            formData.append("speed", String(form.speed));
            // Iter-30.15 — Bug : on forçait "Content-Type: multipart/form-data" SANS boundary,
            // empêchant axios d'auto-injecter la boundary nécessaire au parsing côté backend.
            // Conséquence : la requête traînait ou échouait silencieusement après timeout.
            // Solution : on laisse axios détecter FormData et générer le bon Content-Type.
            const res = await api.post("/admin/onboarding/audio-preview", formData, {
                responseType: "blob",
                timeout: 90000, // ElevenLabs peut prendre ~30s pour ~3000 chars
            });
            const audio = new Audio(URL.createObjectURL(res.data));
            audio.onended = () => setPlaying(false);
            audio.onpause = () => {
                if (audio.currentTime >= audio.duration - 0.1) setPlaying(false);
            };
            handleRef.current.attach(audio);
            audioRef.current = audio;
            await audio.play();
            setPlaying(true);
        } catch {
            toast.error("Preview impossible — vérifie le texte et la voix");
        } finally {
            setPreviewing(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put("/admin/onboarding/audio-settings", {
                text: form.text,
                voice_id: form.voice_id || null,
                speed: form.speed,
            });
            toast.success("Message de bienvenue mis à jour — les nouveaux recruteurs entendront ta version !");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Sauvegarde impossible";
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleResetText = () => {
        setForm((f) => ({ ...f, text: defaults.text }));
        toast.success("Texte par défaut restauré (non sauvegardé)");
    };

    if (loading) {
        return (
            <div className="glass-card rounded-3xl p-6 sm:p-8 animate-pulse">
                <div className="h-6 w-64 bg-white/10 rounded mb-4" />
                <div className="space-y-3">
                    <div className="h-32 bg-white/5 rounded-2xl" />
                    <div className="h-16 bg-white/5 rounded-2xl" />
                </div>
            </div>
        );
    }

    const charCount = (form.text || "").length;
    const charLimit = 4000;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-3xl p-6 sm:p-8"
            data-testid="onboarding-audio-settings"
        >
            <div className="flex items-start gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5 text-shariti-orange-light" />
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-extrabold text-white tracking-tight">
                        Message audio de bienvenue
                    </h3>
                    <p className="text-sm text-white/55 font-light leading-relaxed mt-1">
                        Personnalise le message audio que chaque nouveau recruteur entend dans son
                        onboarding. Texte, voix, vitesse — tout est éditable.
                    </p>
                </div>
            </div>

            {/* Textarea — message */}
            <div className="mb-4">
                <div className="flex items-baseline justify-between mb-1.5">
                    <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-shariti-orange-light">
                        Texte du message · prononcé par la voix sélectionnée
                    </label>
                    <button
                        onClick={handleResetText}
                        type="button"
                        className="text-[10px] font-bold text-white/40 hover:text-shariti-orange-light flex items-center gap-1"
                        title="Restaurer le texte Shariti par défaut"
                    >
                        <RotateCcw className="w-3 h-3" /> Texte par défaut
                    </button>
                </div>
                <textarea
                    value={form.text}
                    onChange={(e) => setForm({ ...form, text: e.target.value })}
                    data-testid="onboarding-audio-text"
                    rows={10}
                    maxLength={charLimit}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-shariti-orange/60 font-light leading-relaxed"
                    placeholder="Écris ton message de bienvenue Shariti..."
                />
                <div className="flex justify-between text-[10px] font-light text-white/35 mt-1">
                    <span>~{Math.max(1, Math.round(charCount / 14))} sec d'audio estimé</span>
                    <span className={charCount > charLimit - 200 ? "text-shariti-orange-light" : ""}>
                        {charCount} / {charLimit}
                    </span>
                </div>
            </div>

            {/* Voice picker + speed */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                    <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-shariti-orange-light mb-1.5 block">
                        Voix · onboarding
                    </label>
                    {/* Iter-27.61 — ThemedSelect Shariti (au lieu du <select> natif blanc) */}
                    <ThemedSelect
                        value={form.voice_id || ""}
                        onChange={(v) => setForm({ ...form, voice_id: v })}
                        placeholder={`Voix Aurore par défaut (${defaults.voice_id?.slice(0, 10) || ""}…)`}
                        dataTestid="onboarding-audio-voice"
                        options={[
                            {
                                value: "",
                                label: `— Voix Aurore par défaut (${defaults.voice_id?.slice(0, 10) || ""}…) —`,
                            },
                            ...voices.map((v) => ({
                                value: v.voice_id,
                                label: `${v.is_custom ? "✦ " : ""}${v.name}${v.gender ? ` · ${v.gender}` : ""}${v.descriptive ? ` · ${v.descriptive}` : ""}`,
                            })),
                        ]}
                    />
                </div>
                <div>
                    <div className="flex items-baseline justify-between mb-1.5">
                        <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-shariti-orange-light">
                            Vitesse
                        </label>
                        <span className="text-[11px] font-extrabold text-shariti-orange-light tabular-nums">
                            {Number(form.speed || 1.05).toFixed(2)}×
                        </span>
                    </div>
                    <input
                        type="range"
                        min="0.7"
                        max="1.2"
                        step="0.05"
                        value={form.speed}
                        onChange={(e) => setForm({ ...form, speed: parseFloat(e.target.value) })}
                        data-testid="onboarding-audio-speed"
                        className="w-full accent-shariti-orange mt-3"
                    />
                </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={handlePreview}
                    disabled={previewing || !form.text || form.text.length < 20}
                    data-testid="onboarding-audio-preview"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/12 text-white/85 hover:text-white text-xs font-bold border border-shariti-orange/30 disabled:opacity-40 transition-all"
                >
                    {previewing ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : playing ? (
                        <Pause className="w-3.5 h-3.5" />
                    ) : (
                        <Play className="w-3.5 h-3.5" />
                    )}
                    {playing ? "Pause preview" : "Tester (preview)"}
                </button>
                <button
                    onClick={handleSave}
                    disabled={saving || !form.text || form.text.length < 20}
                    data-testid="onboarding-audio-save"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-shariti-orange text-white font-extrabold text-xs hover:bg-shariti-orange-light disabled:opacity-50 orange-glow transition-all"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Enregistrer
                </button>
                <p className="text-[10px] font-light text-white/40 ml-auto self-center">
                    <Mic className="w-3 h-3 inline mr-1" /> Synthétisé par ElevenLabs · cache MongoDB
                </p>
            </div>
        </motion.div>
    );
}
